import { useMemo } from "react";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { SpecimenIdentifierPreview } from "../../services/workflows.service";

interface SpecimenIdentifierWarningModalProps {
  preview?: SpecimenIdentifierPreview;
  isSaving: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function SpecimenIdentifierWarningModal({
  preview,
  isSaving,
  error,
  onCancel,
  onConfirm,
}: SpecimenIdentifierWarningModalProps) {
  const changesLabel = useMemo(
    () =>
      preview?.changes
        .map((change) => `${change.before} → ${change.after}`)
        .join(", "),
    [preview]
  );
  return (
    <Modal
      opened={!!preview}
      onClose={onCancel}
      title="Reprint affected labels"
      centered
      withinPortal
      size="md"
      closeOnEscape={!isSaving}
      closeOnClickOutside={!isSaving}
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
          role="alert"
        >
          These specimens already have printed labels. After saving, reprint all
          affected slides, stickers, cassette labels and other labels with the
          new specimen identifiers. Saving does not print them automatically.
        </Alert>
        <Text size="sm">{changesLabel}</Text>
        {error && (
          <Alert color="red" title="Could not save" role="alert">
            {error}
          </Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isSaving}>
            Confirm and Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
